/**
 * VisitClockButton — EVV clock in / clock out for a booked RN visit
 * (appointments tagged assessment_kind) straight from the Schedule card.
 * Reuses the standard EVV endpoints so the visit shows up on the EVV page.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Loader2, LogIn, LogOut, MapPin, Timer } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported by this browser"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => reject(new Error("Location required for EVV. Please enable GPS and try again.")),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

export default function VisitClockButton({ appointment, onChanged }) {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const isAssigned = appointment?.employee_id && appointment.employee_id === user?.id;
  const [visit, setVisit] = useState(undefined); // undefined = loading, null = none
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => {
    axios.get(`${API}/evv/visits`, { params: { appointment_id: appointment.id } })
      .then((r) => setVisit((r.data || [])[0] || null))
      .catch(() => setVisit(null));
  }, [appointment.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (visit?.status !== "in_progress") return;
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [visit]);

  if (!appointment?.assessment_kind || visit === undefined) return null;
  if (!(isAssigned || role === "admin") && !visit) return null;

  const clockIn = async () => {
    setBusy(true);
    try {
      const location = await getLocation();
      const r = await axios.post(`${API}/evv/clock-in`, { appointment_id: appointment.id, location, notes: `RN ${appointment.assessment_kind === "supervisory" ? "supervisory visit" : "re-admission assessment"}` });
      setVisit(r.data);
      toast.success(`Clocked in at ${fmtTime(r.data.clock_in_time)} — EVV is recording this visit`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not clock in");
    } finally {
      setBusy(false);
    }
  };

  const clockOut = async () => {
    setBusy(true);
    try {
      const location = await getLocation();
      const r = await axios.post(`${API}/evv/clock-out`, { visit_id: visit.id, location, services_provided: appointment.assessment_kind === "supervisory" ? "RN Supervisory Visit" : "RN Re-Admission Assessment" });
      setVisit(r.data);
      toast.success(`Clocked out — ${r.data.duration_minutes} min captured by EVV`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not clock out");
    } finally {
      setBusy(false);
    }
  };

  if (!visit) {
    if (appointment.status !== "scheduled" || !isAssigned) return null;
    return (
      <Button size="sm" variant="outline" onClick={clockIn} disabled={busy} className="gap-1 border-teal-300 text-teal-800 hover:bg-teal-50" data-testid={`appt-clock-in-${appointment.id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />} Clock in (EVV)
      </Button>
    );
  }

  if (visit.status === "in_progress") {
    const mins = Math.max(0, Math.floor((now - new Date(visit.clock_in_time).getTime()) / 60000));
    const mine = visit.employee_id === user?.id || role === "admin";
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid={`appt-evv-active-${appointment.id}`}>
        <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 text-teal-800 border border-teal-200 px-2 py-0.5 text-[10px] font-medium">
          <Timer className="w-3 h-3" /> Clocked in {fmtTime(visit.clock_in_time)} · {mins} min
        </span>
        {mine && (
          <Button size="sm" onClick={clockOut} disabled={busy} className="gap-1 bg-teal-600 hover:bg-teal-700" data-testid={`appt-clock-out-${appointment.id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />} Clock out
          </Button>
        )}
      </div>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium" data-testid={`appt-evv-done-${appointment.id}`}>
      <MapPin className="w-3 h-3" /> EVV {fmtTime(visit.clock_in_time)}–{fmtTime(visit.clock_out_time)} · {visit.duration_minutes} min
    </span>
  );
}
